import type { ITabProps } from './Tab'

import { styled } from '@linaria/react'
import { TextBoxSC } from 'atoms/TextBox'
import { MEDIA } from 'vars/index'

export const TabBaseSC = styled.div<
  Omit<ITabProps, 'title' | 'color'> & { colorDefault: ITabProps['color'] }
>`
  display: inline-flex;
  max-width: max-content;
  padding: ${({ paddings }) =>
    paddings && paddings.length > 0
      ? paddings.map((item) => `var(${item})`)?.join(' ')
      : 0};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  position: relative;
  background-color: ${({
    selected,
    disabled,
    background,
    backgroundSelected,
    backgroundDisabled,
    backgroundSelectedDisabled,
  }) =>
    selected && disabled
      ? `var(${backgroundSelectedDisabled})`
      : selected
        ? `var(${backgroundSelected})`
        : disabled
          ? `var(${backgroundDisabled})`
          : `var(${background})`};

  border-radius: ${({ corners }) => `var(${corners})`};

  ${MEDIA.NO_TOUCH} {
    &:hover {
      background-color: ${({
        selected,
        disabled,
        backgroundHovered,
        backgroundDisabled,
        backgroundSelectedHovered,
      }) =>
        disabled
          ? `var(${backgroundDisabled})`
          : selected
            ? `var(${backgroundSelectedHovered})`
            : `var(${backgroundHovered})`};

      ${TextBoxSC} {
        color: ${({
          colorHovered,
          colorDisabled,
          disabled,
          selected,
          colorSelectedHovered,
        }) =>
          disabled
            ? `var(${colorDisabled})`
            : selected
              ? `var(${colorSelectedHovered})`
              : `var(${colorHovered})`};
      }

      ${MEDIA.TOUCH} {
        background-color: ${({
          selected,
          disabled,
          background,
          backgroundSelected,
          backgroundDisabled,
          backgroundSelectedDisabled,
        }) =>
          selected && disabled
            ? `var(${backgroundSelectedDisabled})`
            : selected
              ? `var(${backgroundSelected})`
              : disabled
                ? `var(${backgroundDisabled})`
                : `var(${background})`};

        ${TextBoxSC} {
          color: ${({
            colorDefault,
            colorDisabled,
            disabled,
            selected,
            colorSelected,
            colorSelectedDisabled,
          }) =>
            disabled && selected
              ? `var(${colorSelectedDisabled})`
              : disabled
                ? `var(${colorDisabled})`
                : selected
                  ? `var(${colorSelected})`
                  : `var(${colorDefault})`};
        }
      }
    }
  }

  &:active {
    background-color: ${({
      selected,
      disabled,
      backgroundPressed,
      backgroundDisabled,
      backgroundSelectedPressed,
    }) =>
      disabled
        ? `var(${backgroundDisabled})`
        : selected
          ? `var(${backgroundSelectedPressed})`
          : `var(${backgroundPressed})`};

    ${TextBoxSC} {
      color: ${({
        colorPressed,
        colorDisabled,
        disabled,
        selected,
        colorSelectedPressed,
      }) =>
        disabled
          ? `var(${colorDisabled})`
          : selected
            ? `var(${colorSelectedPressed})`
            : `var(${colorPressed})`};
    }

    ${MEDIA.TOUCH} {
      background-color: ${({
        selected,
        disabled,
        background,
        backgroundSelected,
        backgroundDisabled,
        backgroundSelectedDisabled,
      }) =>
        selected && disabled
          ? `var(${backgroundSelectedDisabled})`
          : selected
            ? `var(${backgroundSelected})`
            : disabled
              ? `var(${backgroundDisabled})`
              : `var(${background})`};

      ${TextBoxSC} {
        color: ${({
          colorDefault,
          colorDisabled,
          disabled,
          selected,
          colorSelected,
          colorSelectedDisabled,
        }) =>
          disabled && selected
            ? `var(${colorSelectedDisabled})`
            : disabled
              ? `var(${colorDisabled})`
              : selected
                ? `var(${colorSelected})`
                : `var(${colorDefault})`};
      }
    }
  }

  ${MEDIA.TOUCH} {
    background-color: ${({
      selected,
      disabled,
      background,
      backgroundSelected,
      backgroundDisabled,
      backgroundSelectedDisabled,
    }) =>
      selected && disabled
        ? `var(${backgroundSelectedDisabled})`
        : selected
          ? `var(${backgroundSelected})`
          : disabled
            ? `var(${backgroundDisabled})`
            : `var(${background})`};

    ${TextBoxSC} {
      color: ${({
        colorDefault,
        colorDisabled,
        disabled,
        selected,
        colorSelected,
        colorSelectedDisabled,
      }) =>
        disabled && selected
          ? `var(${colorSelectedDisabled})`
          : disabled
            ? `var(${colorDisabled})`
            : selected
              ? `var(${colorSelected})`
              : `var(${colorDefault})`};
    }
  }

  &:before {
    content: '';
    display: ${({ selected, gradient, gradientSelected }) =>
      gradient || (selected && gradientSelected) ? 'block' : 'none'};
    background: ${({ selected, gradient, gradientSelected }) =>
      selected ? `var(${gradientSelected})` : `var(${gradient})`};
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    opacity: 1;
    z-index: 0;
  }

  ${MEDIA.NO_TOUCH} {
    &:hover:before {
      opacity: 0;
    }
  }

  ${MEDIA.TOUCH} {
    &:hover:before {
      opacity: 1;
    }
  }

  ${TextBoxSC} {
    color: ${({
      colorDefault,
      colorDisabled,
      disabled,
      selected,
      colorSelected,
      colorSelectedDisabled,
    }) =>
      disabled && selected
        ? `var(${colorSelectedDisabled})`
        : disabled
          ? `var(${colorDisabled})`
          : selected
            ? `var(${colorSelected})`
            : `var(${colorDefault})`};
    z-index: 1;
  }
`

export const TabSimpleBorderSC = styled(TabBaseSC)`
  border: none;
  border-width: ${({ borders }) =>
    borders && borders.length > 0
      ? borders.map((item) => `var(${item})`)?.join(' ')
      : 0};
  border-style: solid;
  border-color: ${({
    borderColor,
    selected,
    disabled,
    borderColorDisabled,
    borderColorSelected,
    borderColorSelectedDisabled,
  }) =>
    disabled && selected
      ? `var(${borderColorSelectedDisabled})`
      : selected
        ? `var(${borderColorSelected})`
        : disabled
          ? `var(${borderColorDisabled})`
          : `var(${borderColor})`};
`

export const TabGradientBorderSC = styled(TabBaseSC)`
  border-image: ${({
    borderColor,
    selected,
    disabled,
    borderColorDisabled,
    borderColorSelected,
    borderColorSelectedDisabled,
  }) =>
    disabled && selected
      ? `var(${borderColorSelectedDisabled})`
      : selected
        ? `var(${borderColorSelected})`
        : disabled
          ? `var(${borderColorDisabled})`
          : `var(${borderColor})`};

  border-image-slice: 1;
  border-width: 0;
  border-style: solid;
  border-image-width: ${({ borders }) =>
    borders && borders.length > 0
      ? borders.map((item) => `var(${item})`)?.join(' ')
      : 0};
`
